/**
 * @description Compares two objects for deep equality.
 * This function checks if two objects are equal by comparing their properties and values recursively.
 * It handles various data types including arrays, dates, regular expressions, sets, maps, and array buffers.
 *
 * @example
 * ```typescript
 * const obj1 = { a: 1, b: { c: 2 } };
 * const obj2 = { a: 1, b: { c: 2 } };
 * console.log(deepEqual(obj1, obj2)); // true
 * ```
 *
 * @param obj1
 * @param obj2
 * @returns true if the objects are equal, false otherwise
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;

  if (obj1 === null || obj2 === null) {
    return obj1 === obj2; // Ensure both are null
  }

  if (obj1 === undefined || obj2 === undefined) {
    return obj1 === obj2; // Ensure both are undefined
  }

  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  if (obj1 instanceof RegExp && obj2 instanceof RegExp) {
    return obj1.toString() === obj2.toString();
  }

  if (obj1 instanceof ArrayBuffer && obj2 instanceof ArrayBuffer) {
    if (obj1.byteLength !== obj2.byteLength) return false;
    const view1 = new Uint8Array(obj1);
    const view2 = new Uint8Array(obj2);
    return view1.every((val, index) => val === view2[index]);
  }

  if (ArrayBuffer.isView(obj1) && ArrayBuffer.isView(obj2)) {
    if (obj1.constructor !== obj2.constructor) return false; // Ensure both are the same TypedArray type
    if (obj1.byteLength !== obj2.byteLength) return false;

    const typedArray1 = new Uint8Array(
      obj1.buffer,
      obj1.byteOffset,
      obj1.byteLength
    );
    const typedArray2 = new Uint8Array(
      obj2.buffer,
      obj2.byteOffset,
      obj2.byteLength
    );

    return typedArray1.every((val, index) => val === typedArray2[index]);
  }

  if (obj1 instanceof Set && obj2 instanceof Set) {
    if (obj1.size !== obj2.size) return false;
    return [...obj1].every((item) => obj2.has(item));
  }

  if (obj1 instanceof Map && obj2 instanceof Map) {
    if (obj1.size !== obj2.size) return false;
    return [...obj1.entries()].every(
      ([key, value]) => obj2.has(key) && deepEqual(value, obj2.get(key))
    );
  }

  if (Object.getPrototypeOf(obj1) !== Object.getPrototypeOf(obj2)) {
    return false; // Objects must have the same prototype
  }

  if (
    typeof obj1 === 'object' &&
    typeof obj2 === 'object' &&
    obj1 !== null &&
    obj2 !== null
  ) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    return keys1.every(
      (key) => keys2.includes(key) && deepEqual(obj1[key], obj2[key])
    );
  }

  return false;
};
